import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import * as React from 'react';

import type { TaskActivity } from '@/services/api/generated/taskActivityService';
import { getActorDisplayName } from '@/utils/actorFormatting';

import { formatUpdateDate } from '../taskUpdateUtils';
import { getTaskEventConfig } from './timelineConfig';

interface TaskEventProps {
  event: TaskActivity['event'];
  defaultCollapsed?: boolean;
}

/**
 * Renders a standalone task event (without a linked reason comment).
 *
 * This is a config-driven component that handles multiple event types:
 *
 * Field change events:
 * - task_status_changed
 * - task_assigned
 * - task_due_date_changed
 * - task_priority_changed
 *
 * Creation events:
 * - task_created
 *
 * The rendering (title, value formatting) is determined by the
 * configuration in timelineConfig.ts based on the event_code.
 *
 * Visual structure for field changes:
 * ┌─────────────────────────────────────────────────────────────┐
 * │ Actor · Jan 9, 5:34 PM                                      │
 * │ Status Changed: Pending → In Progress                       │
 * └─────────────────────────────────────────────────────────────┘
 *
 * Visual structure for creation events:
 * ┌─────────────────────────────────────────────────────────────┐
 * │ Actor · Jan 9, 5:34 PM                                      │
 * │ Task Created: Due Jan 15, 2025                              │
 * └─────────────────────────────────────────────────────────────┘
 *
 * @see TimelineItemCorrelated - for field changes with a linked reason comment
 * @see timelineConfig - for the event type configuration registry
 */
export const TimelineItemEvent: React.FC<TaskEventProps> = ({ event, defaultCollapsed = false }) => {
  const config = getTaskEventConfig(event.event_code);
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);

  if (!config) {
    return null;
  }

  const { payload, occurred_at, user, agent } = event;

  // Format actor name and time
  const actorName = getActorDisplayName(user, agent);
  const formattedTime = occurred_at ? formatUpdateDate(occurred_at) : '';

  const getSummary = () => {
    if (config.type === 'field_change') {
      const rawPayload = payload as { old?: unknown; new?: unknown };
      const oldValue = config.formatOldValue(rawPayload);
      const newValue = config.formatNewValue(rawPayload);
      return `${config.title}: ${oldValue} → ${newValue}`;
    }
    const details = config.formatDetails(
      payload as { task?: { due_date?: string | null } },
    );
    return details ? `${config.title}: ${details}` : config.title;
  };

  if (isCollapsed) {
    return (
      <div className="group border-b border-slate-100 py-1.5 px-2 hover:bg-slate-50 dark:border-zinc-800 dark:hover:bg-zinc-800/50 transition-colors">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setIsCollapsed(false)} className="flex items-center gap-1 text-slate-500 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors shrink-0">
            <ChevronRightIcon className="size-3.5" />
          </button>
          <span className="text-xs font-semibold text-slate-500 dark:text-zinc-400">{actorName}</span>
          <span className="text-slate-400 dark:text-zinc-500">·</span>
          <span className="text-xs text-slate-400 dark:text-zinc-500">{formattedTime}</span>
          <span className="text-xs text-slate-500 dark:text-zinc-400 truncate">— {getSummary()}</span>
        </div>
      </div>
    );
  }

  // Render based on event type
  const renderContent = () => {
    if (config.type === 'field_change') {
      // Field change events have old → new values
      const rawPayload = payload as { old?: unknown; new?: unknown };
      const oldValue = config.formatOldValue(rawPayload);
      const newValue = config.formatNewValue(rawPayload);

      return (
        <p className="mt-1 text-xs font-medium text-slate-600 dark:text-zinc-300">
          {config.title}:{' '}
          <span className="text-slate-400 dark:text-zinc-500">{oldValue}</span>
          {' → '}
          <span className="font-medium text-slate-700 dark:text-zinc-200">
            {newValue}
          </span>
        </p>
      );
    }

    // Creation events have optional details
    const details = config.formatDetails(
      payload as { task?: { due_date?: string | null } },
    );

    return (
      <p className="mt-1 text-xs font-medium text-slate-600 dark:text-zinc-300">
        {config.title}
        {details && (
          <>
            :{' '}
            <span className="font-medium text-slate-700 dark:text-zinc-200">
              {details}
            </span>
          </>
        )}
      </p>
    );
  };

  return (
    <div className="group border-b border-slate-100 py-2 px-2 hover:bg-slate-50 dark:border-zinc-800 dark:hover:bg-zinc-800/50 transition-colors">
      {/* Author and timestamp */}
      <div className="flex items-center gap-2">
        {defaultCollapsed && (
          <button type="button" onClick={() => setIsCollapsed(true)} className="text-slate-500 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors shrink-0">
            <ChevronDownIcon className="size-3.5" />
          </button>
        )}
        <span className="text-xs font-semibold text-slate-900 dark:text-zinc-100">
          {actorName}
        </span>
        <span className="text-slate-400 dark:text-zinc-500">·</span>
        <span className="text-xs text-slate-400 dark:text-zinc-500">
          {formattedTime}
        </span>
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  );
};
